// SlideCard.js - WITH PROGRESSIVE RENDERING FIX
import React, { useState, useRef, useEffect } from 'react';
import { renderSlideToCanvas } from '../canvas/SlideCanvasRenderer';
import './SlideCard.css';

function SlideCard({ 
  slide, 
  index, 
  uploadMode,
  currentTemplate,
  filteredTemplatesList,
  searchTerm,
  onSearchChange,
  onTemplateSelect,
  hasImage,
  canvasRef,
  fontsLoaded,
  onTemplatePrev,
  onTemplateNext,
  formatTime,
  layoutFilter,
  onLayoutFilterChange,
  onImageUpload,
  imageUrl
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const dropdownRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const localCanvasRef = useRef(null);
  
  // ✅ NEW: Track if slide is visible and if canvas has been rendered
  const [isVisible, setIsVisible] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const observerRef = useRef(null);

  const safeFilteredList = filteredTemplatesList || [];
  
  const currentFilteredIndex = safeFilteredList.findIndex(
    t => t?.id === currentTemplate?.id
  );

  // ✅ NEW: Intersection Observer - only render when slide is visible
  useEffect(() => {
    if (!localCanvasRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before entering viewport
        threshold: 0.01
      }
    );

    observerRef.current.observe(localCanvasRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // ✅ UPDATED: Only render when visible AND fonts loaded
  useEffect(() => {
    if (!fontsLoaded || !localCanvasRef.current || !isVisible || hasRendered) return;
    
    const renderCanvas = async () => {
      try {
        await renderSlideToCanvas(
          localCanvasRef.current,
          slide.extractedContent,
          currentTemplate?.id || 'modern_bullets_right',
          imageUrl
        );
        setHasRendered(true);
      } catch (error) {
      
      }
    };

    renderCanvas();
  }, [isVisible, fontsLoaded, currentTemplate?.id, imageUrl, slide.extractedContent, index, hasRendered]);

  // ✅ NEW: When template/image changes, allow re-render
  useEffect(() => {
    if (hasRendered) {
      setHasRendered(false);
    }
  }, [currentTemplate?.id, imageUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value) => {
    onSearchChange(value);
    setShowDropdown(value.trim().length > 0);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    setShowDropdown(false);
  };

  const handleTemplateClick = (template) => {
    onTemplateSelect(index, template.id);
    onSearchChange('');
    setShowDropdown(false);
  };

  const handleSearchFocus = () => {
    if (searchTerm.trim().length > 0) {
      setShowDropdown(true);
    }
  };

  const handleLayoutChange = (newLayout) => {
    onLayoutFilterChange(index, newLayout);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      await onImageUpload(index, file);
    } catch (error) {
      
      alert(`Failed to upload image: ${error.message}`);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="slide-card">
      <div className="slide-card-header">
        <h3>Slide #{index + 1}</h3>
        {uploadMode === 'video' && slide.timestamp !== undefined && (
          <span className="slide-timestamp">{formatTime(slide.timestamp)}</span>
        )}
      </div>

      <div className="template-carousel">
        <div className="template-carousel-header">
          <span className="template-label">Design:</span>
          
          <span className="template-name">{currentTemplate?.name || 'Unknown'}</span>
          
          <div className="template-count">
            <span>{currentFilteredIndex >= 0 ? currentFilteredIndex + 1 : 0} / {safeFilteredList.length}</span>
          </div>

          <div className="template-search-wrapper" ref={searchWrapperRef}>
            <input
              type="text"
              className="template-search"
              placeholder="Search Designs..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={handleSearchFocus}
            />
            {searchTerm && (
              <button 
                className="search-clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            
            {showDropdown && safeFilteredList.length > 0 && (
              <div className="template-dropdown" ref={dropdownRef}>
                {safeFilteredList.map((template) => (
                  <div
                    key={template.id}
                    className={`template-dropdown-item ${
                      template.id === currentTemplate?.id ? 'active' : ''
                    }`}
                    onClick={() => handleTemplateClick(template)}
                  >
                    <span className="template-dropdown-name">{template.name}</span>
                    {template.id === currentTemplate?.id && (
                      <span className="template-dropdown-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showDropdown && safeFilteredList.length === 0 && (
              <div className="template-dropdown">
                <div className="template-dropdown-empty">
                  No templates found
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="template-carousel-container">
          <button
            className="carousel-nav prev"
            onClick={() => onTemplatePrev(index)}
            disabled={!fontsLoaded || safeFilteredList.length <= 1}
            aria-label="Previous template"
          >
            ‹
          </button>

          <div className="template-preview-wrapper">
            {/* ✅ NEW: Show loading state while not visible or not rendered */}
            {!isVisible || !hasRendered ? (
              <div className="slide-loading-skeleton">
                <div className="skeleton-shimmer"></div>
              </div>
            ) : null}
            
            <canvas
              ref={el => {
                localCanvasRef.current = el;
                canvasRef(el);
              }}
              className="template-preview-canvas"
              style={{ opacity: hasRendered ? 1 : 0 }}
            />
          </div>

          <button
            className="carousel-nav next"
            onClick={() => onTemplateNext(index)}
            disabled={!fontsLoaded || safeFilteredList.length <= 1}
            aria-label="Next template"
          >
            ›
          </button>
        </div>

        <div className="layout-filter-section">
          <div className="layout-filter-buttons">
            <button
              className={`layout-filter-btn ${layoutFilter === 'left' ? 'active' : ''}`}
              onClick={() => handleLayoutChange('left')}
              disabled={!fontsLoaded || uploadingImage}
              title="Image on left side"
            >
              <span className="filter-text">Image Left</span>
            </button>

            <button
              className={`layout-filter-btn ${layoutFilter === 'none' ? 'active' : ''}`}
              onClick={() => handleLayoutChange('none')}
              disabled={!fontsLoaded || uploadingImage}
              title="No image, full width content"
            >
              <span className="filter-text">No Image</span>
            </button>

            <button
              className={`layout-filter-btn ${layoutFilter === 'right' ? 'active' : ''}`}
              onClick={() => handleLayoutChange('right')}
              disabled={!fontsLoaded || uploadingImage}
              title="Image on right side"
            >
              <span className="filter-text">Image Right</span>
            </button>

            <button
              className="layout-filter-btn upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={!fontsLoaded || uploadingImage}
              title="Upload custom image"
            >
              {uploadingImage ? (
                <>
                  <span className="upload-spinner">⟳</span>
                  <span className="filter-text">Uploading...</span>
                </>
              ) : (
                <>
                  <span className="filter-icon"></span>
                  <span className="filter-text">Upload Image</span>
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SlideCard;